// Best-effort client-side buffer that batches analytics events and POSTs them
// to /api/analytics/ingest. The server forwards them into Kinesis Firehose,
// which writes gzipped NDJSON to S3 for our own raw event archive.
// Mirrors Segment's envelope shape so historical (segment-data-production) and
// new archives are unionable in Athena/Snowflake.
import { buildFreeToolEnvelope, getFreeToolUsage } from './freeToolPrivacy';

export type AnalyticsEventType = 'track' | 'identify' | 'page' | 'screen' | 'alias' | 'group';

export interface AnalyticsEnvelope {
  type: AnalyticsEventType;
  sentAt?: string;
  timestamp: string;
  originalTimestamp: string;
  anonymousId: string;
  userId: string | null;
  event?: string;
  properties?: Record<string, unknown>;
  traits?: Record<string, unknown>;
  context: {
    page?: { path?: string; referrer?: string; search?: string; title?: string; url?: string };
    userAgent?: string;
    locale?: string;
    library: { name: 'qwa-analytics-shim'; version: string };
  };
  channel: 'client';
}

const LIBRARY_VERSION = '1.0.0';
const FLUSH_EVENT_THRESHOLD = 20;
const FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFER_SIZE = 500; // Firehose PutRecordBatch limit
const RETRY_DELAY_MS = 1000;
// Browser fetch spec caps total in-flight `keepalive` body size at 64 KiB per
// user agent. We use keepalive when the payload fits (so an in-flight request
// survives a navigation race) and fall back to non-keepalive only for the rare
// oversized batch that would otherwise reject synchronously and be lost.
const KEEPALIVE_BODY_LIMIT_BYTES = 60_000; // 4 KiB headroom under the 64 KiB spec cap

const isBrowser = () => typeof window !== 'undefined';
const getIngestUrl = () => process.env.NEXT_PUBLIC_ANALYTICS_INGEST_URL || '';

let buffer: AnalyticsEnvelope[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let lifecycleListenersRegistered = false;

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
};

const cancelFlush = () => {
  if (!flushTimer) return;
  clearTimeout(flushTimer);
  flushTimer = null;
};

const sendWithBeacon = (url: string, payload: string): boolean => {
  if (!isBrowser() || typeof navigator.sendBeacon !== 'function') return false;
  try {
    const blob = new Blob([payload], { type: 'application/json' });
    return navigator.sendBeacon(url, blob);
  } catch {
    return false;
  }
};

const sendWithFetch = async (
  url: string,
  payload: string,
  opts: { keepalive?: boolean } = {},
  attempt = 0
): Promise<boolean> => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'application/json' },
      keepalive: !!opts.keepalive,
      credentials: 'same-origin',
    });
    return response.ok;
  } catch {
    if (attempt === 0) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      return sendWithFetch(url, payload, opts, attempt + 1);
    }
    return false;
  }
};

export const flush = async (options: { sync?: boolean } = {}): Promise<void> => {
  const url = getIngestUrl();
  if (!url || buffer.length === 0) {
    cancelFlush();
    return;
  }

  const events = buffer;
  buffer = [];
  cancelFlush();

  const payload = JSON.stringify({ events: events.map((e) => ({ ...e, sentAt: new Date().toISOString() })) });
  // Use keepalive when the payload fits — so a fetch in-flight when the user
  // navigates is not canceled by the browser. Skip keepalive only for oversized
  // batches that would otherwise reject synchronously against the 64 KiB cap.
  const fitsKeepalive = new Blob([payload]).size < KEEPALIVE_BODY_LIMIT_BYTES;

  if (options.sync) {
    if (sendWithBeacon(url, payload)) return;
    // Beacon failed (e.g., over its ~64 KB cap or unavailable). Keepalive only
    // when the payload fits; otherwise best-effort non-keepalive — may be
    // canceled by navigation, but better than a synchronous keepalive reject.
    await sendWithFetch(url, payload, { keepalive: fitsKeepalive });
    return;
  }
  await sendWithFetch(url, payload, { keepalive: fitsKeepalive });
};

const registerLifecycleListeners = () => {
  if (lifecycleListenersRegistered || !isBrowser()) return;
  lifecycleListenersRegistered = true;

  // Last chance to flush on tab close / navigation
  window.addEventListener('pagehide', () => {
    void flush({ sync: true });
  });
  window.addEventListener('beforeunload', () => {
    void flush({ sync: true });
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush({ sync: true });
  });
};

export const initFirehoseSink = (): void => {
  if (!isBrowser() || !getIngestUrl()) return;
  registerLifecycleListeners();
};

export const enqueueAnalyticsEvent = (envelope: AnalyticsEnvelope): void => {
  if (!isBrowser() || !getIngestUrl()) return;

  // This boundary also covers direct callers that do not use buildEnvelope.
  const usage = getFreeToolUsage(envelope, window.location.pathname);
  buffer.push(usage ? buildFreeToolEnvelope(envelope, usage) : envelope);

  if (buffer.length >= MAX_BUFFER_SIZE) {
    // Hard cap — drop oldest if we're overwhelmed (should never happen under normal load)
    buffer.splice(0, buffer.length - MAX_BUFFER_SIZE);
  }

  if (buffer.length >= FLUSH_EVENT_THRESHOLD) {
    void flush();
    return;
  }

  scheduleFlush();
};

type EnvelopeInput = {
  type: AnalyticsEventType;
  anonymousId: string;
  userId: string | null;
  event?: string;
  properties?: Record<string, unknown>;
  traits?: Record<string, unknown>;
};

export const buildEnvelope = (input: EnvelopeInput): AnalyticsEnvelope => {
  const now = new Date().toISOString();
  const envelope: AnalyticsEnvelope = {
    type: input.type,
    timestamp: now,
    originalTimestamp: now,
    anonymousId: input.anonymousId,
    userId: input.userId,
    context: {
      library: { name: 'qwa-analytics-shim', version: LIBRARY_VERSION },
    },
    channel: 'client',
  };

  if (input.event) envelope.event = input.event;
  if (input.properties) envelope.properties = input.properties;
  if (input.traits) envelope.traits = input.traits;

  if (isBrowser()) {
    envelope.context.userAgent = navigator.userAgent;
    envelope.context.locale = navigator.language;
    envelope.context.page = {
      path: window.location.pathname,
      referrer: document.referrer,
      search: window.location.search,
      title: document.title,
      url: window.location.href,
    };
  }

  const usage = getFreeToolUsage(envelope);
  return usage ? buildFreeToolEnvelope(envelope, usage) : envelope;
};

// Test-only: reset internal state between cases. No production caller.
export const __resetForTests = (): void => {
  buffer = [];
  cancelFlush();
  lifecycleListenersRegistered = false;
};
